import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import ShowLightText from "../../../common/SmallButUseFull/ShowLightText";
import RenderHtml from "../../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import UnderlineUser, {
  registerUserClickHandler,
  clickHandlers,
} from "../../../common/SmallButUseFull/UnderlineUser";
import { CloseIcon } from "../../../common/SVGs";
import useRevisionBasedOnText from "./useRevisionBasedOnText";
import useRevisionCurrentText from "./useRevisionCurrentText";
import { convertUtcToFormattedTime } from "../../../util/AppUtils";
import { fetchUsers } from "../../../redux/userSlice";
import { BASE_URL } from "../../../config";
import Rebased from "./Rebased";

/**
 * DocumentHeader - Header section for document pages
 * Displays version info, title, description, status, and revision
 * Handles basedOnText, currentText, and approvers display logic internally
 */
const DocumentHeader = ({
  // Document Info
  type,
  partNumber,
  revision,
  revisionId,
  partId,
  description,
  status,

  // Revision Data (for generating version texts and approvers)
  currentRevision = null,
  revisionData = [], // Array of all revisions to check base revision status

  // Navigation Data (optional - for checkbox click)
  onCheckboxClick = null,

  // Styling
  showCheckbox = true,
  showVersionInfo = true,
  isCheckboxEnabled = true,
  isCheckboxChecked = false, // Controlled checkbox state
  checkboxLabelColor = null, // Optional color for checkbox label (e.g., "rgb(0, 102, 204)")

  // Additional info container control
  showAdditionalInfo: showAdditionalInfoProp = null,
  onShowAdditionalInfoChange = null,

  // Lock error message
  lockErrorMessage = null,

  // When true, hide type (item code) in title and show only part number
  hideTypeInTitle = false,
  // When true, show status value in grey instead of orange (e.g. from audit trail)
  useGreyStatusColor = false,
  magicUrl = false,
  // When true, hide the "Revision … Release History" block (e.g. training checkmark flow)
  hideRevisionReleaseHistory = false,
  fromAuditTrail = false
}) => {
  // Debug: Log when lockErrorMessage prop is received


  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { users: usersList } = useSelector((state) => state.users || {});
  const [selectedUserInfo, setSelectedUserInfo] = useState(null);
  const [versionData, setVersionData] = useState(null);

  // Use controlled state if provided, otherwise use internal state
  const [internalShowAdditionalInfo, setInternalShowAdditionalInfo] =
    useState(false);
  const showAdditionalInfo =
    showAdditionalInfoProp !== null
      ? showAdditionalInfoProp
      : internalShowAdditionalInfo;
  const setShowAdditionalInfo =
    onShowAdditionalInfoChange || setInternalShowAdditionalInfo;
  const [containerPosition, setContainerPosition] = useState({
    top: 0,
    left: 0,
  });
  const containerRef = useRef(null);

  // Normalize signature data from API (can arrive hex-prefixed or without header)
  const normalizeSignatureDataUrl = (raw) => {
    if (!raw || typeof raw !== "string") return "";
    let value = raw;
    if (value.startsWith("\\x")) {
      const hex = value.slice(2);
      let out = "";
      for (let i = 0; i < hex.length; i += 2) {
        const byte = parseInt(hex.slice(i, i + 2), 16);
        if (!Number.isNaN(byte)) {
          out += String.fromCharCode(byte);
        }
      }
      value = out;
    }
    if (value.startsWith("data:image")) return value;
    if (/^[A-Za-z0-9+/=]+$/.test(value)) {
      return `data:image/png;base64,${value}`;
    }
    return value;
  };

  // Function to convert URLs in text to clickable links
  const renderTextWithLinks = (text) => {
    if (!text) return text;

    // Regex to match URLs (http, https, www)
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        // Ensure URL has protocol
        const url = part.startsWith("http") ? part : `https://${part}`;
        return (
          <a
            key={index}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#000",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {part}
          </a>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  // Generate version texts using hooks
  const basedOnText = useRevisionBasedOnText(currentRevision, revisionData);
  const currentText = useRevisionCurrentText(currentRevision, magicUrl);

  // Ensure users are available for lookup
  useEffect(() => {
    if (!usersList || usersList.length === 0) {
      dispatch(fetchUsers({ limit: 1000, offset: 0 }));
    }
  }, [usersList, dispatch]);

  const buildNameSearchQuery = (name) => {
    const params = new URLSearchParams();
    params.set("limit", "20");
    params.set("offset", "0");
    params.set("order_by", "name");
    params.set("order", "asc");
    if (name) {
      params.set("search_type", "name");
      params.set("search", name);
    }
    return params.toString();
  };

  const handleUserClick = useCallback(
    async (
      userId,
      fallbackName,
      event,
      statusContext = null,
      statusChangeTimestamp = null,
    ) => {

      // If usersList is empty, fetch by searching the user name instead of fetching all
      let currentUsersList = usersList;
      if (!currentUsersList || currentUsersList.length === 0) {
        try {
          const searchQuery = buildNameSearchQuery(fallbackName);
          const result = await dispatch(
            fetchUsers({ limit: 20, offset: 0, searchQuery, withoutToken: magicUrl })
          ).unwrap();
          currentUsersList = result.users || [];
        } catch (error) {
          console.error("Error fetching users via search:", error);
          currentUsersList = [];
        }
      }

      // Get the position of the clicked element
      let top = 0;
      let left = 0;
      if (event && event.target) {
        // Get the actual span element that contains the text
        let targetElement = event.target;
        // If the target is not the span, try to find it
        if (
          targetElement.tagName !== "SPAN" ||
          !targetElement.style.textDecoration
        ) {
          // Look for the span with underline style in the event path
          const path = event.composedPath ? event.composedPath() : [];
          targetElement =
            path.find(
              (el) =>
                el.tagName === "SPAN" &&
                (el.style?.textDecoration === "underline" ||
                  window
                    .getComputedStyle(el)
                    .textDecoration.includes("underline")),
            ) || targetElement;
        }

        const rect = targetElement.getBoundingClientRect();
        const containerWidth = 420; // container width

        // Get the parent container's position (the div with position: relative)
        const parentRect = containerRef.current?.getBoundingClientRect() || {
          top: 0,
          left: 0,
        };

        // Calculate position relative to parent container
        // Position below the clicked element with a small offset
        top = rect.bottom - parentRect.top + 8; // 8px gap below

        // Right edge of container should align with first character of user name
        // So: container's right edge = rect.left (first character position)
        // Therefore: left = rect.left - containerWidth (relative to parent)
        left = rect.left - parentRect.left - containerWidth;

        const viewportWidth = window.innerWidth;
        const minMargin = 20;

        // If container would go off the left edge, adjust but try to maintain alignment
        if (left < minMargin) {
          // If we can't fit the full container, at least align the right edge
          // But if even that would go off screen, position it normally
          if (rect.left + containerWidth > viewportWidth - minMargin) {
            // Container is too wide, position it to the left of the name but within viewport
            left = Math.max(
              minMargin,
              viewportWidth - containerWidth - minMargin,
            );
          } else {
            // Try to maintain right edge alignment
            left = rect.left - containerWidth;
            if (left < minMargin) {
              // If still off screen, position it so right edge is at first character
              // This means left might be negative, so we'll clamp it
              left = Math.max(minMargin, rect.left - containerWidth);
            }
          }
        }

        // Ensure it doesn't go off the right edge (if container would extend beyond viewport)
        if (left + containerWidth > viewportWidth - minMargin) {
          // If container would go off right edge, align right edge to viewport edge
          left = viewportWidth - containerWidth - minMargin;
        }

        // Ensure it doesn't go off the bottom edge (if container would be too tall)
        const estimatedHeight = 150; // smaller estimated container height
        const viewportHeight = window.innerHeight;
        if (top + estimatedHeight > viewportHeight) {
          // Position above the element instead
          top = rect.top - estimatedHeight - 8;
          if (top < minMargin) {
            top = minMargin; // If still too high, position at top with margin
          }
        }
      }

      // Ensure usersList is an array - use currentUsersList (may have been fetched)
      const validUsersList = Array.isArray(currentUsersList)
        ? currentUsersList
        : [];

      // Try to find user by ID first (if userId is provided)
      let foundUser = null;
      if (userId && validUsersList.length > 0) {
        foundUser =
          validUsersList.find(
            (u) =>
              String(u.id) === String(userId) ||
              String(u.user_id) === String(userId) ||
              String(u?.user_ref) === String(userId),
          ) || null;
      }

      // If not found by ID and we have a name, try to find by name
      if (!foundUser && fallbackName && validUsersList.length > 0) {
        const nameParts = fallbackName.trim().split(/\s+/);
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        foundUser =
          validUsersList.find((u) => {
            const uFirstName = (u.first_name || "").trim();
            const uLastName = (u.last_name || "").trim();
            const fullName = `${uFirstName} ${uLastName}`.trim();
            const searchName = fallbackName.trim();
            return (
              (uFirstName.toLowerCase() === firstName.toLowerCase() &&
                uLastName.toLowerCase() === lastName.toLowerCase()) ||
              fullName.toLowerCase() === searchName.toLowerCase()
            );
          }) || null;
      }

      const name =
        (foundUser
          ? `${foundUser.first_name || ""} ${foundUser.last_name || ""}`.trim()
          : fallbackName) || "Unknown User";

      // Check if user_information exists and is not empty
      const userInformation =
        foundUser?.user_information && foundUser.user_information.trim() !== ""
          ? foundUser.user_information
          : "No additional information available for this user.";

      // Find matching audit trail entry for signature based on status context
      // IMPORTANT: Ensure we only look at status changes from the current revision
      let signature = null;

      if (currentRevision && revisionId) {
        // Verify that currentRevision matches the current revisionId to avoid cross-revision signature lookup
        // Use String() to ensure type consistency for comparison
        const currentRevisionId = String(
          currentRevision.procedure_revision_id || "",
        );
        const currentRevisionIdFromProp = String(revisionId || "");

        if (currentRevisionId !== currentRevisionIdFromProp) {
          console.warn(
            `Revision mismatch detected! Current revisionId: ${currentRevisionIdFromProp}, currentRevision.procedure_revision_id: ${currentRevisionId}, currentRevision.rev: ${currentRevision.rev}. Skipping signature lookup to avoid wrong revision signature.`,
          );
          signature = null;
        } else {
          // Map status context to status_changed_to or purpose
          let targetStatus = null;
          let targetPurpose = null;

          if (statusContext) {
            // If statusContext is a status_changed_to value
            if (typeof statusContext === "string") {
              // Check if it's a purpose (Approve, Submit, Release)
              if (["Approve", "Submit", "Release"].includes(statusContext)) {
                targetPurpose = statusContext;
              } else {
                // It's a status_changed_to value
                targetStatus = statusContext;
              }
            } else if (statusContext.status_changed_to) {
              targetStatus = statusContext.status_changed_to;
            } else if (statusContext.purpose) {
              targetPurpose = statusContext.purpose;
            }
          }

          // First, check status_changes for matching user AND status/purpose
          // Ensure we only look at status changes from the current revision
          if (currentRevision.status_changes) {
            // Filter matching status changes
            const matchingStatusChanges = currentRevision.status_changes.filter(
              (statusChange) => {
                // Additional safeguard: ensure this status change belongs to the current revision
                // by checking if it's in the currentRevision's status_changes array
                const auditUser = statusChange.audit_trail?.user;
                const auditUserId =
                  auditUser?.id || auditUser?.user_id || auditUser?.user_ref;
                const auditUserName = auditUser
                  ? `${auditUser.first_name || ""} ${
                      auditUser.last_name || ""
                    }`.trim()
                  : "";

                // Match user
                const userMatches =
                  (userId &&
                    auditUserId &&
                    (auditUserId === userId ||
                      auditUserId === foundUser?.id)) ||
                  (fallbackName &&
                    auditUserName &&
                    auditUserName === fallbackName.trim());

                if (!userMatches) return false;

                // Match by timestamp if provided (required if provided)
                let timestampMatches = true; // Default to true if no timestamp provided
                if (statusChangeTimestamp) {
                  // Normalize timestamps for comparison (handle timezone differences)
                  const normalizeTimestamp = (ts) => {
                    if (!ts) return null;
                    // If timestamp doesn't have timezone info, treat as UTC
                    const dateStr = String(ts);
                    if (
                      !dateStr.endsWith("Z") &&
                      !dateStr.match(/[+-]\d{2}:\d{2}$/)
                    ) {
                      return new Date(dateStr + "Z").getTime();
                    }
                    return new Date(dateStr).getTime();
                  };

                  const targetTimestamp = normalizeTimestamp(
                    statusChangeTimestamp,
                  );
                  const changeTimestamp = normalizeTimestamp(
                    statusChange.created_at,
                  );

                  // Allow small difference (within 1 second) to account for rounding/precision differences
                  if (targetTimestamp && changeTimestamp) {
                    const timeDiff = Math.abs(
                      targetTimestamp - changeTimestamp,
                    );
                    if (timeDiff <= 1000) {
                      // Timestamp matches (within 1 second)
                      timestampMatches = true;
                    } else {
                      // More than 1 second differ
                      return false;
                    }
                  } else {
                    timestampMatches = false;
                  }
                }

                // All conditions must be met: timestamp (if provided) AND status/purpose (if provided)
                // If status context is provided, also match the status
                if (targetStatus) {
                  return (
                    timestampMatches &&
                    statusChange.status_changed_to === targetStatus
                  );
                }

                // If purpose context is provided, check if status change matches the purpose
                if (targetPurpose) {
                  // Map purpose to status_changed_to
                  const purposeToStatus = {
                    Submit: "Submitted For Approval",
                    Approve: "Approved",
                    Release: "Released",
                  };
                  const expectedStatus = purposeToStatus[targetPurpose];
                  return (
                    timestampMatches &&
                    statusChange.status_changed_to === expectedStatus
                  );
                }

                // No status/purpose context, but timestamp must match if provided
                return timestampMatches;
              },
            );

            // If multiple matches and status context is provided, get the most recent one
            // (sorted by created_at descending)
            let matchingStatusChange =
              matchingStatusChanges.length > 0
                ? matchingStatusChanges.sort(
                  (a, b) =>
                      new Date(b.created_at || 0) - new Date(a.created_at || 0),
                )[0]
                : null;

            // If no match found with exact status but we have a purpose and timestamp,
            // try fallback: find any status change by user and timestamp (within tolerance)
            // This handles cases where approval action might have different status
            if (
              !matchingStatusChange &&
              targetPurpose &&
              statusChangeTimestamp &&
              currentRevision.status_changes
            ) {
              const normalizeTimestamp = (ts) => {
                if (!ts) return null;
                const dateStr = String(ts);
                if (
                  !dateStr.endsWith("Z") &&
                  !dateStr.match(/[+-]\d{2}:\d{2}$/)
                ) {
                  return new Date(dateStr + "Z").getTime();
                }
                return new Date(dateStr).getTime();
              };

              const targetTimestamp = normalizeTimestamp(statusChangeTimestamp);

              matchingStatusChange = currentRevision.status_changes.find(
                (statusChange) => {
                  const auditUser = statusChange.audit_trail?.user;
                  const auditUserId =
                    auditUser?.id || auditUser?.user_id || auditUser?.user_ref;
                  const userMatches =
                    (userId &&
                      auditUserId &&
                      (auditUserId === userId ||
                        auditUserId === foundUser?.id)) ||
                    (fallbackName &&
                      auditUser &&
                      `${auditUser.first_name || ""} ${
                        auditUser.last_name || ""
                        }`.trim() === fallbackName.trim());

                  if (!userMatches) return false;

                  // Match by timestamp only (within 1 second tolerance)
                  const changeTimestamp = normalizeTimestamp(
                    statusChange.created_at,
                  );
                  if (targetTimestamp && changeTimestamp) {
                    const timeDiff = Math.abs(
                      targetTimestamp - changeTimestamp,
                    );
                    return timeDiff <= 1000;
                  }
                  return false;
                },
              );

              if (matchingStatusChange) {
              
              }
            }

            if (matchingStatusChange?.audit_trail?.signature) {
              const rawSignature = matchingStatusChange.audit_trail.signature;
              signature = normalizeSignatureDataUrl(rawSignature);
            }
          }

          // If not found in status_changes and we have a purpose context, check required_by
          if (!signature && targetPurpose && currentRevision.required_by) {
            const matchingRequiredBy = currentRevision.required_by.find(
              (req) => {
                const reqUser = req.user;
                const reqUserId =
                  reqUser?.id || reqUser?.user_id || reqUser?.user_ref;
                const reqUserName = reqUser
                  ? `${reqUser.first_name || ""} ${
                      reqUser.last_name || ""
                    }`.trim()
                  : "";
                const userMatches =
                  (userId &&
                    reqUserId &&
                    (reqUserId === userId || reqUserId === foundUser?.id)) ||
                  (fallbackName &&
                    reqUserName &&
                    reqUserName === fallbackName.trim());

                // Also match the purpose
                return userMatches && req.purpose === targetPurpose;
              },
            );

            // If found in required_by, try to find corresponding status change with signature
            if (matchingRequiredBy && currentRevision.status_changes) {
              // Map purpose to status
              const purposeToStatus = {
                Submit: "Submitted For Approval",
                Approve: "Approved",
                Release: "Released",
              };
              const expectedStatus = purposeToStatus[targetPurpose];

              // Find status change that matches the purpose, status, user, and timestamp (if provided)
              const matchingStatusChange = currentRevision.status_changes.find(
                (statusChange) => {
                  const auditUser = statusChange.audit_trail?.user;
                  const auditUserId =
                    auditUser?.id || auditUser?.user_id || auditUser?.user_ref;
                  const userMatches =
                    auditUserId &&
                    (auditUserId === matchingRequiredBy.user?.id ||
                      auditUserId === matchingRequiredBy.user?.user_id ||
                      auditUserId === matchingRequiredBy.user?.user_ref);

                  if (!userMatches) {
                    return false;
                  }

                  // Both timestamp (if provided) AND status must match
                  // Check timestamp first
                  let timestampMatches = true; // Default to true if no timestamp provided
                  if (statusChangeTimestamp) {
                    const normalizeTimestamp = (ts) => {
                      if (!ts) return null;
                      const dateStr = String(ts);
                      if (
                        !dateStr.endsWith("Z") &&
                        !dateStr.match(/[+-]\d{2}:\d{2}$/)
                      ) {
                        return new Date(dateStr + "Z").getTime();
                      }
                      return new Date(dateStr).getTime();
                    };

                    const targetTimestamp = normalizeTimestamp(
                      statusChangeTimestamp,
                    );
                    const changeTimestamp = normalizeTimestamp(
                      statusChange.created_at,
                    );

                    if (targetTimestamp && changeTimestamp) {
                      const timeDiff = Math.abs(
                        targetTimestamp - changeTimestamp,
                      );
                      if (timeDiff <= 1000) {
                        // Timestamp matches (within 1 second)
                        timestampMatches = true;
                      } else {
                        // Timestamp doesn't match
                        timestampMatches = false;
                      }
                    } else {
                      timestampMatches = false;
                    }
                  }

                  // Both timestamp (if provided) AND status must match
                  return (
                    timestampMatches &&
                    statusChange.status_changed_to === expectedStatus
                  );
                },
              );

              if (matchingStatusChange?.audit_trail?.signature) {
                const rawSignature = matchingStatusChange.audit_trail.signature;
                signature = normalizeSignatureDataUrl(rawSignature);
              }
            }
          }
        }
      }

      setSelectedUserInfo({
        name,
        user_information: userInformation,
        signature,
      });
      setContainerPosition({ top, left });
      setShowAdditionalInfo(true);
    },
    [usersList, currentRevision, revisionId],
  );

  // Register all user click handlers for all status changes (after handleUserClick is defined)
  useEffect(() => {
   
    // Clear all existing handlers when revision changes to avoid stale handlers
    // This ensures we only have handlers for the current revision
    if (currentRevision && revisionId) {
      const currentRevisionId = String(
        currentRevision.procedure_revision_id || "",
      );
      const currentRevisionIdFromProp = String(revisionId || "");

      // Only proceed if revision IDs match
      if (currentRevisionId !== currentRevisionIdFromProp) {
        console.warn(
          `Skipping handler registration - revision mismatch. revisionId: ${currentRevisionIdFromProp}, currentRevision.procedure_revision_id: ${currentRevisionId}`,
        );
        return;
      }

      // Clear all handlers before registering new ones to prevent stale handlers from previous revisions
      clickHandlers.clear();
      

      // Register handlers for all status changes
      const sortedStatusChanges = [
        ...(currentRevision.status_changes || []),
      ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      sortedStatusChanges.forEach((statusChange) => {
        const user = statusChange.audit_trail?.user;
        const name = user ? `${user.first_name} ${user.last_name}` : "";
        const userId =
          user?.id || user?.user_id || user?.user_ref || statusChange.user_id;

        if (name) {
          const clickHandler = (e) =>
            handleUserClick(
              userId,
              name,
              e,
              statusChange.status_changed_to,
              statusChange.created_at,
            );
          registerUserClickHandler(name, clickHandler);
         
        }
      });

      // Register handlers for approvers (if status is approved)
      if (status?.toLowerCase() === "approved" && currentRevision.required_by) {
        currentRevision.required_by
          ?.filter((s) => s.purpose === "Approve")
          .forEach((d) => {
            if (d.user) {
              const userName = `${d.user.first_name} ${d.user.last_name}`;
              const fullName = `${d.user?.first_name || ""} ${
                d.user?.last_name || ""
                }`.trim();
              const clickHandler = (e) =>
                handleUserClick(d.user?.id, fullName, e, "Approve");
              registerUserClickHandler(userName, clickHandler);
            }
          });
      }
    }
  }, [currentRevision, status, handleUserClick, revisionId]);

  // Generate approvers list for "Approved" status
  const approvers = [];
  if (currentRevision && status?.toLowerCase() === "approved") {
    currentRevision.required_by
      ?.filter((s) => s.purpose === "Approve")
      .sort((a, b) => new Date(a.updated_at) - new Date(b.updated_at))
      .forEach((d) => {
        approvers.push(
          <ShowLightText
            key={d.user?.id || Math.random()}
            text={
              <>
                Approved: {convertUtcToFormattedTime(d.updated_at)}
                {(() => {
                  const userName = d.user?.first_name + " " + d.user?.last_name;
                  const fullName = `${d.user?.first_name || ""} ${
                    d.user?.last_name || ""
                    }`.trim();
                  const clickHandler = (e) =>
                    handleUserClick(
                      d.user?.id,
                      fullName,
                      e,
                      "Approve",
                      d.updated_at,
                    );
                  // Register handler by name as backup
                  registerUserClickHandler(userName, clickHandler);
                  return (
                    <UnderlineUser name={userName} onClick={clickHandler} disabled={magicUrl}/>
                  );
                })()}
              </>
            }
          />,
        );
      });
  }

  // Call version/get-all API when status is released
  useEffect(() => {
    if (
      status?.toLowerCase() === "released" ||
      status?.toLowerCase() === "archived"
    ) {
      const versionId = currentRevision?.based_on_version_ref;
      const fetchVersionData = async () => {
        try {
          const response = await axios.get(`${BASE_URL}version/get-all`, {
            params: { version_id: versionId },
          });
          setVersionData(response.data);
        } catch (error) {
          console.error("Error fetching version data:", error);
          setVersionData(null);
        }
      };
      fetchVersionData();
    } else {
      setVersionData(null);
    }
  }, [status, currentRevision]);
  // Generate release history for "Released" and "Archived" statuses
  const releaseHistory = [];
  if (
    currentRevision &&
    (status?.toLowerCase() === "released" ||
      status?.toLowerCase() === "archived")
  ) {
    let oldestRebasedItem = null;
    // Filter and add rebased items from versionData
    if (versionData) {
      const versionDataArray = Array.isArray(versionData?.results)
        ? versionData.results
        : Array.isArray(versionData)
          ? versionData
          : [];

      const rebasedItems = versionDataArray.filter(
        (vdt) => vdt?.version_content?.rebase_status,
      );

      // Find latest rebased item based on audit_updated_at
      if (rebasedItems.length > 0) {
        const latestRebasedItem = rebasedItems.reduce((latest, rbi) => {
          const latestDate = latest?.audit_updated_at
            ? new Date(latest.audit_updated_at).getTime()
            : 0;
          const currentDate = rbi?.audit_updated_at
            ? new Date(rbi.audit_updated_at).getTime()
            : 0;
          return currentDate > latestDate ? rbi : latest;
        });

        // Find oldest rebased item based on audit_updated_at
        oldestRebasedItem = rebasedItems.reduce((oldest, rbi) => {
          const oldestDate = oldest?.audit_updated_at
            ? new Date(oldest.audit_updated_at).getTime()
            : Infinity;
          const currentDate = rbi?.audit_updated_at
            ? new Date(rbi.audit_updated_at).getTime()
            : Infinity;
          return currentDate < oldestDate ? rbi : oldest;
        });
      }

      rebasedItems.forEach((vdt) => {
        releaseHistory.push({
          text: (
            <Rebased
              key={`rebased-${vdt.id || vdt.audit_updated_at || Math.random()}`}
              vdt={vdt}
            />
          ),
          created_at: vdt.audit_updated_at,
        });
      });
    }
    // Always use status_changes from currentRevision for status change history
    const statusChanges = currentRevision.status_changes || [];

    // Sort status changes by created_at to show chronological history
    const sortedStatusChanges = [...statusChanges].sort(
      (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
    );

    sortedStatusChanges.forEach((statusChange, index) => {
      const user = statusChange.audit_trail?.user;
      const name = user ? `${user.first_name} ${user.last_name}` : "";
      const userId =
        user?.id || user?.user_id || user?.user_ref || statusChange.user_id;

      if (
        index === 0 &&
        currentRevision.based_on_version_details.version_content &&
        !currentRevision.based_on_version_details?.version_content
          ?.rebase_status
      ) {
        releaseHistory.push({
          text: (
            <Rebased
              key={`based-${currentRevision.id || currentRevision.audit_updated_at || Math.random()}`}
              vdt={{
                version_content: {
                  partNumber:
                    currentRevision.based_on_version_details.version_content
                      .procedure_number,
                  rev: currentRevision.based_on_version_details.version_content
                    .rev,
                  current_status:
                    currentRevision.based_on_version_details.version_content
                      .current_status,
                },
                audit_updated_at:
                  currentRevision.based_on_version_details.updated_at,
              }}
              showBasedOnText={true}
            />
          ),
          created_at: currentRevision.based_on_version_details.updated_at,
          status: "Not Submitted",
        });
      }

      if (statusChange.status_changed_to === "Not Submitted") {
        releaseHistory.push({
          text: (
            <ShowLightText
              key={statusChange.procedure_revision_status_id || Math.random()}
              text={
                <>
                  {oldestRebasedItem &&
                    oldestRebasedItem.parent_version_data.version_content && (
                      <>
                        Based on{" "}
                        {oldestRebasedItem.parent_version_data.version_content
                          .part_number
                          ? oldestRebasedItem.parent_version_data
                              .version_content.part_number
                          : oldestRebasedItem.parent_version_data
                              .version_content.procedure_number}{" "}
                        Rev{" "}
                        {
                          oldestRebasedItem.parent_version_data.version_content
                            .rev
                        }
                        , When{" "}
                        {
                          oldestRebasedItem.parent_version_data.version_content
                            .current_status
                        }
                        , at{" "}
                        {convertUtcToFormattedTime(statusChange.created_at)}
                        <div style={{ marginTop: "5px" }}></div>
                      </>
                    )}
                  Created on{" "}
                  {convertUtcToFormattedTime(statusChange.created_at)}
                  {name &&
                    (() => {
                      const clickHandler = (e) =>
                        handleUserClick(
                          userId,
                          name,
                          e,
                          statusChange.status_changed_to,
                          statusChange.created_at,
                        );
                      // Register handler by name as backup
                      registerUserClickHandler(name, clickHandler);
                      return (
                        <UnderlineUser name={name} onClick={clickHandler} disabled={magicUrl}/>
                      );
                    })()}
                </>
              }
            />
          ),
          created_at: statusChange.created_at,
          status: statusChange.status_changed_to,
        });
      } else if (statusChange.status_changed_to === "Submitted For Approval") {
        releaseHistory.push({
          text: (
            <ShowLightText
              key={statusChange.procedure_revision_status_id || Math.random()}
              text={
                <>
                  Submitted:{" "}
                  {convertUtcToFormattedTime(statusChange.created_at)}
                  {name &&
                    (() => {
                      const clickHandler = (e) =>
                        handleUserClick(
                          userId,
                          name,
                          e,
                          statusChange.status_changed_to,
                          statusChange.created_at,
                        );
                      // Register handler by name as backup
                      registerUserClickHandler(name, clickHandler);
                      return (
                        <UnderlineUser name={name} onClick={clickHandler} disabled={magicUrl}/>
                      );
                    })()}
                </>
              }
            />
          ),
          created_at: statusChange.created_at,
          status: statusChange.status_changed_to,
        });
      } else if (statusChange.status_changed_to === "Approved") {
        // For Approved status, show all approvers
        const approversList = currentRevision.required_by
          ?.filter((s) => s.purpose === "Approve")
          .sort((a, b) => new Date(a.updated_at) - new Date(b.updated_at))
          .map((d) => (
            <>
              Approved: {convertUtcToFormattedTime(d.updated_at)}
              {(() => {
                const userName = d.user.first_name + " " + d.user.last_name;
                const fullName = `${d.user?.first_name || ""} ${
                  d.user?.last_name || ""
                  }`.trim();
                const clickHandler = (e) =>
                  handleUserClick(
                    d.user?.id,
                    fullName,
                    e,
                    "Approve",
                    d.updated_at,
                  );
                // Register handler by name as backup
                registerUserClickHandler(userName, clickHandler);
                return <UnderlineUser name={userName} onClick={clickHandler} disabled={magicUrl}/>;
              })()}
              &nbsp;&nbsp;&nbsp;
            </>
          ));

        if (approversList && approversList.length > 0) {
          releaseHistory.push({
            text: (
              <ShowLightText
                key={statusChange.procedure_revision_status_id || Math.random()}
                text={
                  <>
                    {approversList.map((approver, idx) => (
                      <span key={idx}>{approver}</span>
                    ))}
                  </>
                }
              />
            ),
            created_at: statusChange.created_at,
            status: statusChange.status_changed_to,
          });
        }
      } else {
        // For other status changes (Released, etc.)
        releaseHistory.push({
          text: (
            <ShowLightText
              key={statusChange.procedure_revision_status_id || Math.random()}
              text={
                <>
                  {statusChange.status_changed_to}:{" "}
                  {convertUtcToFormattedTime(statusChange.created_at)}
                  {name &&
                    (() => {
                      const clickHandler = (e) =>
                        handleUserClick(
                          userId,
                          name,
                          e,
                          statusChange.status_changed_to,
                          statusChange.created_at,
                        );
                      // Register handler by name as backup
                      registerUserClickHandler(name, clickHandler);
                      return (
                        <UnderlineUser name={name} onClick={clickHandler} disabled={magicUrl}/>
                      );
                    })()}
                </>
              }
            />
          ),
          created_at: statusChange.created_at,
          status: statusChange.status_changed_to,
        });
      }
    });
  }

  // Sort releaseHistory by created_at after adding all items (status changes + rebased items)
  releaseHistory.sort((a, b) => {
    if (!a.created_at && !b.created_at) return 0;
    if (!a.created_at) return 1;
    if (!b.created_at) return -1;

    // Normalize dates: if date doesn't have timezone, treat as UTC for consistent comparison
    const normalizeDate = (dateStr) => {
      // If date doesn't end with Z and doesn't have timezone offset, append Z to treat as UTC
      if (
        dateStr &&
        !dateStr.endsWith("Z") &&
        !dateStr.match(/[+-]\d{2}:\d{2}$/)
      ) {
        return new Date(dateStr + "Z").getTime();
      }
      return new Date(dateStr).getTime();
    };

    const dateA = normalizeDate(a.created_at);
    const dateB = normalizeDate(b.created_at);

    return dateA - dateB;
  });

  // Filter out redundant "Submitted" entries.
  // Rules:
  // - Always keep the first "Submitted For Approval".
  // - After that, for each *cluster* of "Submitted For Approval" entries,
  //   keep only the first one that comes after a "Change Requested".
  //   (i.e., if multiple "Submitted" happen without an intervening "Change Requested",
  //   only the first of that group is shown.)
  const displayReleaseHistory = (() => {
    let lastSubmittedIndex = -1;
    let lastChangeRequestedIndex = -1;

    return releaseHistory.filter((item, index) => {
      const itemStatus = item.status;

      if (itemStatus === "Change Requested") {
        lastChangeRequestedIndex = index;
        return true;
      }

      if (itemStatus === "Submitted For Approval") {
        const shouldKeep =
          lastSubmittedIndex === -1 ||
          lastChangeRequestedIndex > lastSubmittedIndex;

        if (shouldKeep) {
          lastSubmittedIndex = index;
        }

        // Drop extra "Submitted For Approval" entries until a "Change Requested" appears
        return shouldKeep;
      }

      return true;
    });
  })();
  /**
   * Handle checkbox click navigation
   * If custom handler provided, use it; otherwise navigate to view-revision
   */
  const handleCheckboxChange = () => {
    if (onCheckboxClick) {
      onCheckboxClick();
    } else if (partId && revisionId) {
      // Default navigation to view-revision route
      navigate(`/view-revision/${partId}/${revisionId}`);
    }
  };

  /** Title label should only navigate when the checkbox is shown and enabled (e.g. not live/view mode). */
  const titleNavigationEnabled = showCheckbox && isCheckboxEnabled;

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {/* Version Info - Show approvers for Approved, otherwise show basedOnText and currentText (but NOT for Released or Archived) */}
      {(showVersionInfo && fromAuditTrail===false) &&
        status?.toLowerCase() !== "released" &&
        status?.toLowerCase() !== "archived" && (
          <>
            {status?.toLowerCase() === "approved" && approvers.length > 0 ? (
              /* For Approved status: show basedOnText and approvers */
              <div
                style={{
                  fontSize: "12px",
                  color: "#444",
                  marginBottom: "8px",
                }}
              >
                {basedOnText && (
                  <div style={{ marginBottom: "5px" }}>{basedOnText}</div>
                )}
                {approvers.map((approver, index) => (
                  <div key={index}>{approver}</div>
                ))}
              </div>
            ) : (
              /* For other statuses: show basedOnText and currentText */
              (basedOnText || currentText) && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#444",
                    marginBottom: "8px",
                  }}
                >
                  {basedOnText && (
                    <div style={{ marginBottom: currentText ? "4px" : "0" }}>
                      {basedOnText}
                    </div>
                  )}
                  {currentText && <div>{currentText}</div>}
                </div>
              )
            )}
          </>
        )}

      {/* Lock Error Message - Show after version info and status messages */}
      {lockErrorMessage && (
        <div
          style={{
            fontSize: "12px",
            color: "#dc3545", // Bootstrap red color
            whiteSpace: "pre-line", // Preserve line breaks
            marginBottom: "12px",
            maxWidth: "100%",
            fontWeight: "500",
          }}
        >
          {lockErrorMessage}
        </div>
      )}

      {/* Floating User Information Container */}
      {showAdditionalInfo && selectedUserInfo && (
        <div
          style={{
            position: "absolute",
            top: `${containerPosition.top}px`,
            left: `${containerPosition.left}px`,
            background: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            padding: "12px 14px",
            width: "420px",
            maxHeight: "400px",
            overflowY: "auto",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            zIndex: 10000,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header with Close Button */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "8px",
              paddingBottom: "6px",
            }}
          >
            <div style={{ fontWeight: 600, fontSize: "16px", color: "#000" }}>
              Additional Information
            </div>
            <button
              onClick={() => setShowAdditionalInfo(false)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4px",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f0f0f0";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
              aria-label="Close"
            >
              <CloseIcon width="14" height="14" color="#6C757D" />
            </button>
          </div>

          {/* Content */}
          <div style={{ flex: 1 }}>
            {/* Signature - Above user information */}
            {selectedUserInfo.signature && (
              <div
                style={{
                  marginBottom: "12px",
                }}
              >
                <div
                  style={{
                    marginBottom: "6px",
                    color: "#000",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  Handwritten Signature
                </div>
                <div
                  style={{
                    backgroundColor: "#F1F1F1",
                    borderRadius: "8px",
                    padding: "10px",
                  }}
                >
                  <div
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      padding: "8px",
                      backgroundColor: "#fff",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: "80px",
                    }}
                  >
                    <img
                      src={selectedUserInfo.signature}
                      alt="User signature"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "120px",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* User Information */}
            <div
              style={{
                marginBottom: "6px",
                color: "#000",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              User Information: {selectedUserInfo.name}
            </div>
            <div
              style={{
                backgroundColor: "#F1F1F1",
                borderRadius: "8px",
                padding: "10px",
              }}
            >
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  color: "#444",
                  fontSize: "14px",
                  lineHeight: "1.4",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  padding: "6px 12px",
                  backgroundColor: "#fff",
                }}
              >
                {renderTextWithLinks(selectedUserInfo.user_information)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "20px",
          padding: "8px 0",
          flexWrap: "wrap",
        }}
      >
        {/* Checkbox + Title + Description */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              margin: 0,
            }}
          >
            {showCheckbox && (
              <input
                type="checkbox"
                checked={isCheckboxChecked}
                onChange={handleCheckboxChange}
                disabled={!isCheckboxEnabled}
              />
            )}
            <span
              onClick={
                titleNavigationEnabled ? handleCheckboxChange : undefined
              }
              style={{
                fontSize: "20px",
                fontWeight: 600,
                color: checkboxLabelColor || undefined,
                cursor: titleNavigationEnabled ? "pointer" : "default",
              }}
            >
              {hideTypeInTitle ? partNumber : `${type} ${partNumber}`}
            </span>
          </div>

          {/* Description */}
          {description && (
            <span
              style={{
                marginLeft: showCheckbox ? "2px" : "0",
                marginTop: "5px",
                color: "#666",
              }}
            >
              Description: <RenderHtml content={description} inline />
            </span>
          )}
        </div>

        {/* Right-aligned section */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            marginLeft: "auto",
          }}
        >
          {status && (
            <div>
              <strong>Status: </strong>
              <span
                style={{
                  color: useGreyStatusColor
                    ? "#212529"
                    : status?.toLowerCase() === "released"
                      ? "#198754"
                      : "orange",
                }}
              >
                {status}
              </span>
            </div>
          )}

          {revision && (
            <div>
              <strong>Revision: </strong>
              <span>{revision}</span>
            </div>
          )}
        </div>
      </div>

      {/* Release History - Show below Description for Released and Archived statuses */}
      {showVersionInfo &&
        !hideRevisionReleaseHistory &&
        (status?.toLowerCase() === "released" ||
          status?.toLowerCase() === "archived") &&
        displayReleaseHistory.length > 0 && (
          <div style={{ marginTop: "10px" }}>
            <div
              style={{
                fontSize: "19px",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Revision {currentRevision?.rev}{" "}
              {status?.toLowerCase() === "archived" ? "Archive" : "Release"}{" "}
              History
            </div>
            {displayReleaseHistory.map((historyItem, index) => (
              <div
                key={`history-${index}-${
                  historyItem.created_at || Date.now()
                  }-${Math.random()}`}
              >
                {historyItem.text}
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

export default DocumentHeader;